import {create} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';

/**
 * Helper to update URL search params without triggering React re-renders.
 * Uses replaceState directly to avoid useSearchParams subscription issues.
 */
const updateUrlParams = (updates) => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);

    Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, String(value));
        }
    });

    window.history.replaceState({}, '', url.toString());
};

/**
 * Zustand store for managing the Generate Initial Version modal state.
 * This allows the modal to be triggered from multiple places:
 * - DisclosureDocumentHeader
 * - DisclosureAIAssistant
 * - CreateDisclosureDialog (after disclosure creation)
 * - Any other component that needs to trigger the generation modal
 *
 * Strategy:
 * - READ from URL only once on mount (via initFromUrl in useGenerateInitialOnMount)
 * - WRITE to URL on isOpen changes (via subscribeWithSelector)
 * - Components subscribe to Zustand, not URL params
 */
const useGenerateInitialVersionStore = create(
    subscribeWithSelector((set, get) => ({
        // Modal visibility
        isOpen: false,

        // Whether initial sync from URL has happened
        _initialized: false,

        // Form state
        instructions: '',
        selectedPolicy: 'default',

        // Parent content data (can be set externally or fetched internally)
        parentContent: null,
        parentReportId: null,
        parentVersion: null,
        parentMeta: null,
        parentLoading: false,
        parentError: null,

        // --- Generated output snapshot (persists across modal close/open) ---
        generatedMarkdown: '',
        reasoningSteps: [],
        isReasoning: false,
        isGenerating: false,
        generatingError: null,

        // Used to ignore stale stream events if a new generation starts
        activeGenerationId: null,

        // Actions
        openModal: (options = {}) => {
            const {
                instructions = '',
                selectedPolicy = 'default',
                parentContent = null,
                parentReportId = null,
                parentVersion = null,
                parentMeta = null,
            } = options;

            // Check if reportId has changed - if so, reset generated content
            const currentState = get();
            const reportIdChanged = parentReportId !== null &&
                currentState.parentReportId !== null &&
                currentState.parentReportId !== parentReportId;

            set({
                isOpen: true,
                _initialized: true,
                instructions,
                selectedPolicy,
                ...(parentContent !== null && {parentContent}),
                ...(parentReportId !== null && {parentReportId}),
                ...(parentVersion !== null && {parentVersion}),
                ...(parentMeta !== null && {parentMeta}),
                // Reset generated content if reportId changed
                ...(reportIdChanged && {
                    generatedMarkdown: '',
                    reasoningSteps: [],
                    isReasoning: false,
                    isGenerating: false,
                    generatingError: null,
                    activeGenerationId: null,
                }),
            });
        },

        closeModal: () => {
            // Intentionally do NOT clear generation state here.
            // Closing should only hide the UI; the streamed generation/output should persist.
            set({isOpen: false});
        },

        // Initialize from URL (called once on mount)
        initFromUrl: (searchParams) => {
            if (get()._initialized) return;

            const generateInitial = searchParams.get('generateInitial') === 'true';
            set({
                isOpen: generateInitial,
                _initialized: true,
            });
        },

        // Update form fields
        setInstructions: (instructions) => set({instructions}),
        setSelectedPolicy: (selectedPolicy) => set({selectedPolicy}),

        // Update parent content data
        setParentData: ({parentContent, parentReportId, parentVersion, parentMeta}) => {
            set({
                parentContent,
                parentReportId,
                parentVersion,
                parentMeta,
            });
        },

        setParentLoading: (loading) => set({parentLoading: loading}),
        setParentError: (error) => set({parentError: error}),

        // --- Generation snapshot actions ---
        setGenerationState: (patch = {}) => set(patch),

        startNewGeneration: () => {
            const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
            set({
                activeGenerationId: id,
                isGenerating: true,
                isReasoning: false,
                generatingError: null,
                generatedMarkdown: '',
                reasoningSteps: [],
            });
            return id;
        },

        resetGenerated: () => set({
            generatedMarkdown: '',
            reasoningSteps: [],
            isReasoning: false,
            isGenerating: false,
            generatingError: null,
            activeGenerationId: null,
        }),

        // Reset the store to initial state
        reset: () => set({
            isOpen: false,
            _initialized: false,
            instructions: '',
            selectedPolicy: 'default',
            parentContent: null,
            parentReportId: null,
            parentVersion: null,
            parentMeta: null,
            parentLoading: false,
            parentError: null,

            generatedMarkdown: '',
            reasoningSteps: [],
            isReasoning: false,
            isGenerating: false,
            generatingError: null,
            activeGenerationId: null,
        }),
    }))
);

// Subscribe to isOpen changes and sync to URL
useGenerateInitialVersionStore.subscribe(
    (state) => state.isOpen,
    (isOpen) => {
        // Only sync after initialization
        if (!useGenerateInitialVersionStore.getState()._initialized) return;

        if (isOpen) {
            updateUrlParams({generateInitial: 'true'});
        } else {
            updateUrlParams({generateInitial: null});
        }
    }
);

export default useGenerateInitialVersionStore;
